import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Image as ImageIcon } from '@phosphor-icons/react';
import { Delete, Edit } from '@mui/icons-material';
import api from '../../utils/api';
import useThemeStyles from '../../theme/useThemeStyles';

const emptyForm = {
  title: '',
  body: '',
  imageUrl: '',
  targetUserType: 'both'
};

function PlatformUpdates() {
  const styles = useThemeStyles();
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/announcements');
      setAnnouncements(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError('Failed to load platform updates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const handleFormChange = (field) => (event) => {
    setForm(prev => ({ ...prev, [field]: event.target.value }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditing(null);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (JPEG, PNG, GIF or WebP)');
      return;
    }
    try {
      setUploadingImage(true);
      setError('');
      const formData = new FormData();
      formData.append('image', file);
      const response = await api.post('/api/admin/announcements/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm(prev => ({ ...prev, imageUrl: response.data.imageUrl }));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      setError('Please add both a title and message.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const payload = {
        title: form.title.trim(),
        body: form.body.trim(),
        imageUrl: form.imageUrl?.trim() || undefined,
        targetUserType: form.targetUserType
      };

      if (editing?._id) {
        await api.put(`/api/admin/announcements/${editing._id}`, payload);
        setSuccess('Platform update saved.');
      } else {
        const response = await api.post('/api/admin/announcements', payload);
        const push = response.data?.push;
        if (push?.sent !== undefined) {
          setSuccess(`Platform update sent to ${push.sent} device(s).`);
        } else {
          setSuccess('Platform update sent.');
        }
      }

      resetForm();
      await loadAnnouncements();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save platform update');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (announcement) => {
    setEditing(announcement);
    setForm({
      title: announcement.title || '',
      body: announcement.body || '',
      imageUrl: announcement.imageUrl || '',
      targetUserType: announcement.targetUserType || 'both'
    });
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      setSaving(true);
      await api.delete(`/api/admin/announcements/${deleteId}`);
      setSuccess('Platform update deleted.');
      if (editing?._id === deleteId) {
        resetForm();
      }
      setDeleteId(null);
      await loadAnnouncements();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete platform update');
    } finally {
      setSaving(false);
    }
  };

  const targetLabel = (value) => (
    value === 'supplier' ? 'Suppliers' : value === 'couple' ? 'Couples' : 'Both'
  );

  return (
    <Box sx={styles.container.page}>
      <Typography component="h2" className="pagename" sx={styles.text.pagename}>
        Platform Updates
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {editing ? 'Edit platform update' : 'New platform update'}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                label="Title"
                fullWidth
                value={form.title}
                onChange={handleFormChange('title')}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Target</InputLabel>
                <Select
                  value={form.targetUserType}
                  label="Target"
                  onChange={handleFormChange('targetUserType')}
                  disabled={saving}
                >
                  <MenuItem value="both">Both</MenuItem>
                  <MenuItem value="couple">Couples</MenuItem>
                  <MenuItem value="supplier">Suppliers</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Message"
                fullWidth
                multiline
                minRows={3}
                value={form.body}
                onChange={handleFormChange('body')}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<ImageIcon size={18} />}
                  disabled={saving || uploadingImage}
                >
                  {uploadingImage ? 'Uploading...' : form.imageUrl ? 'Change image' : 'Upload image'}
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    hidden
                    onChange={handleImageUpload}
                  />
                </Button>
                {form.imageUrl && (
                  <>
                    <Box
                      component="img"
                      src={form.imageUrl}
                      alt="Preview"
                      sx={{ maxHeight: 70, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                    />
                    <Button
                      size="small"
                      color="error"
                      onClick={() => setForm(prev => ({ ...prev, imageUrl: '' }))}
                      disabled={saving}
                    >
                      Remove
                    </Button>
                  </>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                  {saving ? 'Saving...' : editing ? 'Save changes' : 'Send update'}
                </Button>
                {editing && (
                  <Button variant="outlined" onClick={resetForm} disabled={saving}>
                    Cancel
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Sent updates
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Sent</TableCell>
                  <TableCell>Created by</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {announcements.map((announcement) => (
                  <TableRow key={announcement._id}>
                    <TableCell>{announcement.title}</TableCell>
                    <TableCell>{targetLabel(announcement.targetUserType)}</TableCell>
                    <TableCell>{new Date(announcement.createdAt).toLocaleString()}</TableCell>
                    <TableCell>
                      {announcement.createdBy
                        ? `${announcement.createdBy.firstName || ''} ${announcement.createdBy.lastName || ''}`.trim() || announcement.createdBy.email
                        : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => startEdit(announcement)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => setDeleteId(announcement._id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && announcements.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No platform updates yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete platform update?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This removes the record from admin history.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)} disabled={saving}>Cancel</Button>
          <Button color="error" onClick={confirmDelete} disabled={saving}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PlatformUpdates;
